import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cargoApi, type ShipmentListItem } from '@/api/cargo';
import { fmt, num } from '@/shared/format';
import { PageHead, DataTable, StatusBadge, type Col } from '@/shared/ui/kit';
import { SHIPMENT_STATUS, SHIPMENT_STATUS_COLOR, PAYMENT_STATUS_COLOR, toOptions } from '@/shared/cargo';
import { ShipmentDrawer } from './ShipmentDrawer';

export function ShipmentsPage() {
  const [status, setStatus] = useState<number | ''>('');
  const [search, setSearch] = useState('');
  const [editId, setEditId] = useState<string | null | undefined>(undefined); // undefined=closed, null=new

  const q = useQuery({
    queryKey: ['cargo', 'shipments', status],
    queryFn: () => cargoApi.shipments(status === '' ? undefined : status),
  });

  const rows = useMemo(() => {
    const list = q.data ?? [];
    const s = search.trim().toLowerCase();
    if (!s) return list;
    return list.filter(
      (r) =>
        r.TrackingNo.toLowerCase().includes(s) ||
        (r.CustomerName ?? '').toLowerCase().includes(s) ||
        (r.WarehouseCode ?? '').toLowerCase().includes(s),
    );
  }, [q.data, search]);

  const cols: Col<ShipmentListItem>[] = [
    {
      key: 'TrackingNo',
      label: 'Track №',
      width: 150,
      render: (r) => (
        <button className="ct-link" onClick={(e) => { e.stopPropagation(); setEditId(r.Id); }}>
          {r.TrackingNo}
        </button>
      ),
    },
    { key: 'CustomerName', label: 'Харилцагч', render: (r) => r.CustomerName ?? '—' },
    { key: 'WarehouseCode', label: 'Агуулахын код', render: (r) => r.WarehouseCode ?? '—' },
    {
      key: 'Status',
      label: 'Төлөв',
      render: (r) => <StatusBadge label={r.StatusLabel} color={SHIPMENT_STATUS_COLOR[r.Status]} />,
    },
    { key: 'TransportMode', label: 'Тээвэр', render: (r) => r.TransportModeLabel },
    { key: 'WeightKg', label: 'Жин (кг)', align: 'right', render: (r) => num(r.WeightKg) },
    { key: 'Charge', label: 'Төлбөр', align: 'right', render: (r) => fmt(r.Charge) },
    {
      key: 'PaymentStatus',
      label: 'Төлбөр',
      render: (r) => <StatusBadge label={r.PaymentStatusLabel} color={PAYMENT_STATUS_COLOR[r.PaymentStatus]} />,
    },
  ];

  return (
    <div>
      <PageHead
        title="Илгээмж"
        subtitle="Карго илгээмж бүртгэх, хянах, төлөв шилжүүлэх."
        counter={<span className="ts-badge" style={{ background: 'var(--ts-card-bg-2)', color: 'var(--ts-muted)' }}>{rows.length} бүртгэл</span>}
        actions={
          <button className="ts-btn ts-btn-primary" onClick={() => setEditId(null)}>
            <i className="bi bi-plus-lg" /> Шинэ илгээмж
          </button>
        }
      />

      <div className="ts-flex-row" style={{ marginBottom: 14, gap: 10, flexWrap: 'wrap' }}>
        <select className="ts-select" style={{ width: 220 }} value={status} onChange={(e) => setStatus(e.target.value === '' ? '' : Number(e.target.value))}>
          <option value="">Бүх төлөв</option>
          {toOptions(SHIPMENT_STATUS).map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
          <i className="bi bi-search" style={{ position: 'absolute', left: 12, top: 10, color: 'var(--ts-muted)' }} />
          <input className="ts-input" style={{ paddingLeft: 34 }} placeholder="Track №, харилцагч, код хайх..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      <DataTable cols={cols} rows={rows} onRowClick={(r) => setEditId(r.Id)} empty="Илгээмж бүртгэгдээгүй байна." />

      {editId !== undefined && (
        <ShipmentDrawer
          id={editId}
          onClose={() => setEditId(undefined)}
          onSaved={() => { setEditId(undefined); q.refetch(); }}
        />
      )}
    </div>
  );
}
